import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Cell, Legend } from "recharts";
import { Store, ArrowRightLeft, Package, AlertTriangle, TrendingUp, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API_URL as API } from "@/lib/api";
import TCBadge from "@/components/TCBadge";

const COLORS_MK = "#f59e0b";
const COLORS_PB = "#8b5cf6";

const formatBs = (v) => new Intl.NumberFormat('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0) + ' Bs';
const formatUSD = (v) => '$ ' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

const KpiCard = ({ label, mkValue, pbValue, format, icon: Icon }) => (
  <Card className="p-3 border border-border" data-testid={`comp-kpi-${label.toLowerCase().replace(/\s/g,'-')}`}>
    <p className="text-[10px] text-muted-foreground uppercase mb-2 flex items-center gap-1">
      {Icon && <Icon className="h-3 w-3" />} {label}
    </p>
    <div className="grid grid-cols-2 gap-2">
      <div>
        <p className="text-[9px] text-amber-600 font-medium">M. Kapac</p>
        <p className="mono-font text-sm font-bold text-foreground">{format ? format(mkValue) : mkValue?.toLocaleString()}</p>
      </div>
      <div>
        <p className="text-[9px] text-violet-600 font-medium">P. Bonita</p>
        <p className="mono-font text-sm font-bold text-foreground">{format ? format(pbValue) : pbValue?.toLocaleString()}</p>
      </div>
    </div>
  </Card>
);

const ComparativoLocales = ({ tipoCambio }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/comparativo/locales`);
      setData(res.data);
    } catch (err) {
      console.error("Error fetching comparativo:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="comp-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF9900]" />
      </div>
    );
  }

  if (!data?.stores) return <p className="text-center text-muted-foreground py-10">Error cargando datos comparativos</p>;

  const mk = data.stores.manco_kapac || {};
  const pb = data.stores.plaza_bonita || {};

  // Prepare chart data for top families comparison
  const allFamilias = new Set([
    ...(mk.top_familias || []).map(f => f.familia),
    ...(pb.top_familias || []).map(f => f.familia),
  ]);
  const famChartData = Array.from(allFamilias).slice(0, 8).map(fam => {
    const mkF = (mk.top_familias || []).find(f => f.familia === fam);
    const pbF = (pb.top_familias || []).find(f => f.familia === fam);
    return {
      name: (fam || "Sin fam.").slice(0, 15),
      manco_kapac: mkF?.stock || 0,
      plaza_bonita: pbF?.stock || 0,
    };
  }).sort((a, b) => (b.manco_kapac + b.plaza_bonita) - (a.manco_kapac + a.plaza_bonita));

  const disc = data.discrepancies || {};
  const recs = data.restock_recommendations || [];

  return (
    <div className="space-y-4" data-testid="comparativo-locales">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="heading-font text-lg font-bold text-foreground flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-[#FF9900]" /> Comparativo de Locales
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            <span className="text-amber-600 font-medium">Manco Kapac</span> vs <span className="text-violet-600 font-medium">Plaza Bonita</span> — Analisis lado a lado
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tipoCambio && <TCBadge tc={tipoCambio} />}
          <Button variant="outline" size="sm" onClick={fetchData} className="text-xs gap-1.5" data-testid="comp-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Actualizar
          </Button>
        </div>
      </div>

      {/* KPI Comparison */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="comp-kpis">
        <KpiCard label="Productos" mkValue={mk.total_products} pbValue={pb.total_products} icon={Package} />
        <KpiCard label="Uds. Stock" mkValue={mk.total_stock} pbValue={pb.total_stock} icon={Store} />
        <KpiCard label="Valor PMenor" mkValue={mk.valor_pmenor_bs} pbValue={pb.valor_pmenor_bs} format={formatBs} icon={TrendingUp} />
        <KpiCard label="Valor USD" mkValue={mk.valor_pmenor_usd} pbValue={pb.valor_pmenor_usd} format={formatUSD} />
        <KpiCard label="Sin Stock" mkValue={mk.sin_stock} pbValue={pb.sin_stock} icon={AlertTriangle} />
        <KpiCard label="Stock Bajo" mkValue={mk.stock_bajo} pbValue={pb.stock_bajo} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Families Comparison */}
        <Card className="p-4 border border-border" data-testid="comp-chart-families">
          <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-1.5">
            <Package className="h-4 w-4 text-[#FF9900]" /> Stock por Familia
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={famChartData} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 9 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={100} />
              <RTooltip contentStyle={{ fontSize: 11 }} formatter={(v) => v.toLocaleString()} />
              <Legend verticalAlign="top" height={30} iconSize={10} wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="manco_kapac" name="M. Kapac" fill={COLORS_MK} radius={[0, 3, 3, 0]} barSize={10} />
              <Bar dataKey="plaza_bonita" name="P. Bonita" fill={COLORS_PB} radius={[0, 3, 3, 0]} barSize={10} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Discrepancies Summary */}
        <Card className="p-4 border border-border" data-testid="comp-discrepancies">
          <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-1.5">
            <ArrowRightLeft className="h-4 w-4 text-[#FF9900]" /> Discrepancias
          </h3>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 rounded-lg bg-amber-50/60 border border-amber-200">
                <p className="text-2xl font-bold text-amber-700">{disc.only_manco_kapac || 0}</p>
                <p className="text-[10px] text-amber-600 font-medium">Solo en M. Kapac</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-emerald-50/60 border border-emerald-200">
                <p className="text-2xl font-bold text-emerald-700">{disc.common || 0}</p>
                <p className="text-[10px] text-emerald-600 font-medium">En ambos</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-violet-50/60 border border-violet-200">
                <p className="text-2xl font-bold text-violet-700">{disc.only_plaza_bonita || 0}</p>
                <p className="text-[10px] text-violet-600 font-medium">Solo en P. Bonita</p>
              </div>
            </div>

            {/* Sample exclusive products */}
            {disc.only_mk_sample?.length > 0 && (
              <div>
                <p className="text-[10px] text-amber-700 font-semibold mb-1">Exclusivos M. Kapac (muestra):</p>
                <div className="flex flex-wrap gap-1">
                  {disc.only_mk_sample.slice(0, 5).map((p, i) => (
                    <Badge key={i} variant="outline" className="text-[9px] border-amber-300 text-amber-700">
                      {p.codigo} - {(p.descripcion || '').slice(0, 20)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {disc.only_pb_sample?.length > 0 && (
              <div>
                <p className="text-[10px] text-violet-700 font-semibold mb-1">Exclusivos P. Bonita (muestra):</p>
                <div className="flex flex-wrap gap-1">
                  {disc.only_pb_sample.slice(0, 5).map((p, i) => (
                    <Badge key={i} variant="outline" className="text-[9px] border-violet-300 text-violet-700">
                      {p.codigo} - {(p.descripcion || '').slice(0, 20)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Restock Recommendations */}
      {recs.length > 0 && (
        <Card className="p-4 border-2 border-[#FF9900]/30" data-testid="comp-restock">
          <h3 className="text-sm font-bold text-foreground mb-1 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[#FF9900]" /> Recomendaciones de Restock
          </h3>
          <p className="text-xs text-muted-foreground mb-3">Productos con stock alto en un local pero bajo/agotado en el otro</p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/80">
                <TableRow>
                  <TableHead className="text-[11px] font-semibold">Codigo</TableHead>
                  <TableHead className="text-[11px] font-semibold">Descripcion</TableHead>
                  <TableHead className="text-[11px] font-semibold">Familia</TableHead>
                  <TableHead className="text-[11px] font-semibold">Origen</TableHead>
                  <TableHead className="text-[11px] font-semibold">Stock Origen</TableHead>
                  <TableHead className="text-[11px] font-semibold">Destino</TableHead>
                  <TableHead className="text-[11px] font-semibold">Stock Destino</TableHead>
                  <TableHead className="text-[11px] font-semibold">Transferir</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recs.map((r, i) => (
                  <TableRow key={i} className="hover:bg-muted/50" data-testid={`restock-row-${i}`}>
                    <TableCell className="mono-font text-xs font-bold">{r.codigo}</TableCell>
                    <TableCell className="text-xs max-w-[180px] truncate">{r.descripcion}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.familia}</TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] ${r.from === 'manco_kapac' ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-violet-100 text-violet-700 border-violet-300'} border`}>
                        {r.from_name}
                      </Badge>
                    </TableCell>
                    <TableCell className="mono-font text-xs font-bold text-emerald-700">{r.from_stock}</TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] ${r.to === 'manco_kapac' ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-violet-100 text-violet-700 border-violet-300'} border`}>
                        {r.to_name}
                      </Badge>
                    </TableCell>
                    <TableCell className="mono-font text-xs font-bold text-red-600">{r.to_stock}</TableCell>
                    <TableCell>
                      <Badge className="bg-[#FF9900]/10 text-[#FF9900] border-[#FF9900]/30 border text-xs font-bold">
                        {r.suggested_transfer} uds
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ComparativoLocales;
